// SPDX-License-Identifier: Apache-2.0

// VaultExperience — the product surface.
//
// Solid port of the React `vault-experience.tsx`. The orbit is full-page; a
// thin topbar floats over it; a search box sits top-center; clicking a node
// opens the markdown modal.
//
// Drag-drop wraps the whole experience: any file dropped anywhere on the
// page goes through `parseFiles()` from @tether/storage and lands in the
// orbit + Dexie.
//
// Empty state: an FSAccess "pick a folder" CTA + drop hint. Once the user
// drops or picks, we fill the orbit. We never auto-prompt the picker — that
// must come from a click handler.

import { Show, createSignal, createMemo, onCleanup, onMount } from "solid-js";
import { isIngestable, INGEST_ACCEPT, seedDemo, type VaultDoc } from "@tether/storage";
import { VaultOrbit } from "./VaultOrbit";
import { VaultModal } from "./VaultModal";
import { VaultTopbar, type VaultStats } from "./VaultTopbar";
import { useVault } from "./useVault";
import { useSemanticEdges, type SemanticToggle } from "./use-semantic-edges";
import { useBrainDocs } from "./use-brain-docs";
import { setLocale, t, type Locale } from "./i18n";
import { usePresence, pickRandomColor } from "../collab/use-presence";
import { PresenceBar } from "../collab/PresenceBar";
import "./vault.css";

export interface VaultExperienceProps {
  /** Optional collab room id. (v1: parameter is plumbed but not consumed —
   *  the Yjs presence layer ships in a follow-up stream.) */
  roomId?: string;
  /** Lock the locale before any t() call in this render tree. */
  forceLocale?: Locale;
}

export default function VaultExperience(props: VaultExperienceProps) {
  // Lock locale synchronously *before* any t() call; effects are too late.
  if (props.forceLocale) setLocale(props.forceLocale);

  const vault = useVault();

  // Collab presence — Yjs awareness over the YjsSignalRoom DO. When `roomId`
  // is undefined the hook stays inert (empty peers, null self). When it's
  // set, two browser tabs in the same room see each other's avatars within
  // ~2s of connection.
  //
  // Note: in dev (localhost:5173) the Hono MCP on :5417 does NOT mount /yjs.
  // Cross-tab presence requires either pointing VITE_YJS_URL at the deployed
  // CF Worker or running the worker locally with `wrangler dev --env yjs`.
  const presence = usePresence(() => props.roomId);

  const [highlight, setHighlight] = createSignal<Set<string>>(new Set());
  const [hoveredId, setHoveredId] = createSignal<string | null>(null);
  const [dragOver, setDragOver] = createSignal(false);
  const [semanticMode, setSemanticMode] = createSignal<SemanticToggle>("tfidf");
  const [embodiedMode, setEmbodiedMode] = createSignal(false);
  // Orbit layout-mode — VaultOrbit3D accepts a `layoutMode?: () => 'ring' |
  // 'cluster'` prop and createEffects on it via engine.setLayoutMode(mode).
  // Wired through to the orbit at the <VaultOrbit /> call site below.
  const [layoutMode, setLayoutMode] = createSignal<"ring" | "cluster">("ring");
  // Topbar query — owned here, not in VaultSearch. The old VaultSearch
  // component is dropped; we run the same debounced `vault.search()` logic
  // inline so the topbar's input stays the single source of truth.
  const [topbarQuery, setTopbarQuery] = createSignal("");
  let searchDebounce: ReturnType<typeof setTimeout> | null = null;
  let searchEpoch = 0;
  function onTopbarQueryChange(q: string) {
    setTopbarQuery(q);
    if (searchDebounce) clearTimeout(searchDebounce);
    const myEpoch = ++searchEpoch;
    if (q.trim().length === 0) {
      setHighlight(new Set<string>());
      return;
    }
    searchDebounce = setTimeout(async () => {
      try {
        const ids = await vault.search(q, 32);
        if (myEpoch !== searchEpoch) return; // stale
        setHighlight(ids);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[vault-experience] search failed:", err);
        if (myEpoch === searchEpoch) setHighlight(new Set<string>());
      }
    }, 200);
  }
  let dragDepth = 0;

  // Semantic edges — TF-IDF instant by default; user can flip to embeddings
  // (slower, needs the e5-small model) or off via the topbar toggle. Edges
  // pass through to VaultOrbit3D's `semanticEdges` prop, rendered as a dim
  // cyan overlay on top of the wikilink layer.
  const semantic = useSemanticEdges(vault.docs, semanticMode);

  // Brain-agent doc-summary feed (sessions / cost / needs-input). Endpoint
  // may 404 today — the hook degrades to empty state without throwing.
  const brainDocs = useBrainDocs();

  // Stats for the topbar HUD — pure derived state, recomputes on any
  // upstream change.
  const stats = createMemo<VaultStats>(() => {
    const docs = vault.docs();
    const projects = docs.filter((d) => d.front?.type === "project").length;
    const summaries = brainDocs.state().docs;
    const sessions = summaries.filter(
      (s) => s.status === "running" || s.status === "spawning" || s.pid != null,
    ).length;
    const cost = summaries.reduce(
      (acc, s) => acc + (typeof s.cost_usd === "number" ? s.cost_usd : 0),
      0,
    );
    const needsInput = summaries.some(
      (s) => s.status === "awaiting-input" || s.status === "needs-input",
    );
    return {
      docs: docs.length,
      projects,
      sessions,
      cost,
      needsInput,
      ingesting: vault.ingesting(),
    };
  });

  // ── drag-drop ───────────────────────────────────────────────────────────

  function isFilesDrag(e: DragEvent): boolean {
    return Boolean(e.dataTransfer?.types?.includes("Files"));
  }
  function onDragEnter(e: DragEvent) {
    if (!isFilesDrag(e)) return;
    e.preventDefault();
    dragDepth += 1;
    setDragOver(true);
  }
  function onDragOver(e: DragEvent) {
    if (!isFilesDrag(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }
  function onDragLeave(e: DragEvent) {
    if (!isFilesDrag(e)) return;
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDragOver(false);
  }
  async function onDrop(e: DragEvent) {
    if (!isFilesDrag(e)) return;
    e.preventDefault();
    dragDepth = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    if (!files.some(isIngestable)) {
      vault.showToast(t("toast_unsupported"));
      return;
    }
    const report = await vault.ingestFiles(files);
    if (report.firstId) vault.setActiveId(report.firstId);
    const parts: string[] = [];
    if (report.ingested === 1) parts.push(t("toast_added_one"));
    else if (report.ingested > 1) parts.push(t("toast_added_many", { n: report.ingested }));
    if (report.skipped === 1) parts.push(t("toast_skipped_one"));
    else if (report.skipped > 1) parts.push(t("toast_skipped_many", { n: report.skipped }));
    if (parts.length === 0) parts.push(t("toast_none_supported"));
    vault.showToast(parts.join(" · "));
  }

  // Hidden file input (used by the topbar "+ new" button as a fast
  // alternative to FSAccess when the user just wants to drop a single file).
  let fileInputRef: HTMLInputElement | undefined;
  async function onFileInputChange(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    const report = await vault.ingestFiles(files);
    if (report.firstId) vault.setActiveId(report.firstId);
    input.value = ""; // allow same-file re-pick
  }

  // Seed self once on mount so other peers can see us as soon as the room
  // opens. The hook tolerates being called before `roomId()` resolves —
  // pending self-state is flushed when the room binds.
  onMount(() => {
    presence.setSelf({ name: "you", color: pickRandomColor() });
  });

  // ── dev test hook (parity with old vault-experience) ────────────────────
  onMount(() => {
    if (typeof window === "undefined") return;
    if (import.meta.env?.PROD) return;
    const w = window as unknown as { __vaultTest?: Record<string, unknown> };
    w.__vaultTest = {
      getDocs: () =>
        vault.docs().map((d) => ({ id: d.id, title: d.title, tint: d.tint })),
      getActiveId: () => vault.activeId(),
      getMode: () => "solo",
      getRoomId: () => props.roomId ?? null,
      selectById: (id: string) => vault.setActiveId(id),
      selectByIndex: (i: number) =>
        vault.setActiveId(vault.docs()[i]?.id ?? null),
      clearActive: () => vault.setActiveId(null),
      pickFolder: () => vault.pickFolder(),
    };
  });
  onCleanup(() => {
    const w = window as unknown as { __vaultTest?: Record<string, unknown> };
    if (w.__vaultTest) delete w.__vaultTest;
  });

  // ── render ──────────────────────────────────────────────────────────────
  return (
    <div
      class="vault-experience"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="vault-experience"
    >
      <input
        ref={(el) => (fileInputRef = el)}
        type="file"
        multiple
        accept={INGEST_ACCEPT}
        onChange={onFileInputChange}
        data-testid="vault-file-input"
        style={{ display: "none" }}
      />

      {/* Orbit fills the full page, behind the chrome. */}
      <div class="vault-experience__stage">
        <VaultOrbit
          docs={vault.docs}
          activeId={vault.activeId}
          highlight={highlight}
          hoveredId={hoveredId}
          onSelect={(id) => vault.setActiveId(id)}
          onHover={setHoveredId}
          semanticEdges={semantic.edges}
          embodiedMode={embodiedMode}
          layoutMode={layoutMode}
        />
      </div>

      {/* Topbar — dense HUD ported from v1's vault-topbar. Bottom-anchored
          to keep clear of the global lens-tabs nav. */}
      <VaultTopbar
        query={topbarQuery}
        onQueryChange={onTopbarQueryChange}
        layoutMode={layoutMode}
        onLayoutModeChange={setLayoutMode}
        semanticMode={semanticMode}
        onSemanticModeChange={(m) => setSemanticMode(m)}
        semanticProgress={semantic.progress}
        embodiedMode={embodiedMode}
        onEmbodiedToggle={() => setEmbodiedMode((v) => !v)}
        stats={stats}
        onAddFile={() => fileInputRef?.click()}
        onPickFolder={() => void vault.pickFolder()}
        presenceSlot={
          props.roomId ? <PresenceBar peers={presence.peers} /> : undefined
        }
      />

      {/* Empty state — only when the vault has loaded and is empty. */}
      <Show when={vault.ready() && vault.docs().length === 0}>
        <div class="vault-empty" data-testid="vault-empty">
          <div class="vault-empty__card">
            <div class="vault-empty__title">
              {t("drop_anywhere")}
            </div>
            <div class="vault-empty__sub">{t("drop_or_pick")}</div>
            <div class="vault-empty__actions">
              <button
                class="vault-empty__cta"
                onClick={() => fileInputRef?.click()}
              >
                {t("topbar_new")}
              </button>
              <button
                class="vault-empty__cta vault-empty__cta--ghost"
                onClick={() => void vault.pickFolder()}
              >
                {t("pick_folder")}
              </button>
              <button
                class="vault-empty__cta vault-empty__cta--ghost"
                onClick={async () => {
                  const added = await seedDemo(vault.store);
                  if (added === 0) vault.showToast(t("demo_already_present"));
                  else if (added === 1) vault.showToast(t("demo_loaded_one"));
                  else vault.showToast(t("demo_loaded_many", { n: added }));
                }}
              >
                {t("try_demo")}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Hint when content present but nothing selected. */}
      <Show when={vault.ready() && vault.docs().length > 0 && !vault.activeDoc()}>
        <div class="vault-hint">{t("hint_click_panel")}</div>
      </Show>

      {/* Modal when a doc is selected. */}
      <Show when={vault.activeDoc()}>
        {(doc) => (
          <VaultModal
            doc={doc()}
            vault={vault}
            onClose={() => vault.setActiveId(null)}
          />
        )}
      </Show>

      {/* Drop overlay. */}
      <Show when={dragOver()}>
        <div class="vault-dropzone" data-testid="vault-dropzone">
          <div class="vault-dropzone__card">
            <div class="vault-dropzone__heading">{t("dropzone_heading")}</div>
            <div class="vault-dropzone__sub">
              {t("dropzone_formats")}
              <br />
              {t("dropzone_assurance")}
            </div>
          </div>
        </div>
      </Show>

      {/* Toast (ingest progress / status). */}
      <Show when={vault.ingesting() || vault.toast()}>
        <div class="vault-toast" data-testid="vault-toast">
          {vault.ingesting() ? t("toast_ingesting") : vault.toast()}
        </div>
      </Show>
    </div>
  );
}
